<template>
  <div class="hs-meeting" :style="{ height: frameHeight + 'px' }">
    <div v-if="loading" class="loader"></div>
    <iframe
      v-if="utkResolved"
      :src="src"
      :title="title"
      class="hs-meeting__frame"
      @load="onFrameLoad"
    ></iframe>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useResearch } from '@/stores/Research';
import { getCookie } from '@/helper/helper';

const AD_CLICK_PARAMS = ['gclid', 'fbclid', 'msclkid', 'ttclid'];

const props = defineProps({
  meetingUrl: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: 'Schedule a meeting',
  },
  bookingSource: {
    type: String,
    required: true,
  },
});

const loading = ref(true);
// Виджет постит {height: N} только в широкой (desktop) раскладке —
// растягиваем iframe под контент, чтобы не было внутреннего скролла.
// Мобильная раскладка height не шлёт — фиксированный запас, скроллит родитель.
const messagedHeight = ref<number | null>(null);

// HubSpot tracking script (грузится асинхронно через GTM) может выставить
// cookie hubspotutk позже маунта. Без parentHubspotUtk букинг не свяжется
// с контактом — ждём cookie до 3с, потом рендерим iframe как есть.
const utk = ref(getCookie('hubspotutk'));
const utkResolved = ref(Boolean(utk.value));

onMounted(() => {
  if (utkResolved.value) return;
  let tries = 0;
  const timer = setInterval(() => {
    const value = getCookie('hubspotutk');
    tries++;
    if (value || tries >= 15) {
      if (value) utk.value = value;
      utkResolved.value = true;
      clearInterval(timer);
    }
  }, 200);
});

const frameHeight = computed(() => {
  if (messagedHeight.value) return messagedHeight.value;
  return window.innerWidth < 768 ? 850 : 650;
});

function getPrefill() {
  const info = useResearch()?.research?._contact_information;
  return {
    firstName: info?.first_name,
    lastName: info?.last_name,
    email: info?.email,
  };
}

const src = computed(() => {
  const url = new URL(props.meetingUrl);
  url.searchParams.set('embed', 'true');

  const prefill = getPrefill();
  if (prefill.firstName) url.searchParams.set('firstName', prefill.firstName);
  if (prefill.lastName) url.searchParams.set('lastName', prefill.lastName);
  if (prefill.email) url.searchParams.set('email', prefill.email);

  // parentHubspotUtk связывает букинг с существующим контактом HubSpot —
  // без него теряется атрибуция (реплика официального MeetingsEmbedCode.js).
  if (utk.value) url.searchParams.set('parentHubspotUtk', utk.value);
  url.searchParams.set(
    'parentPageUrl',
    window.location.origin + window.location.pathname,
  );
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (/^utm_/i.test(key) || AD_CLICK_PARAMS.includes(key.toLowerCase())) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
});

function gtmPush(data: Record<string, unknown>) {
  if (import.meta.env.VITE_API_ENV !== 'prod') {
    return;
  }
  const dataLayer = ((window as any).dataLayer =
    (window as any).dataLayer || []);
  dataLayer.push(data);
}

function onFrameLoad() {
  loading.value = false;
  gtmPush({
    event: 'hubspot_meeting_widget_viewed',
    booking_type: 'hubspot_meetings',
    booking_source: props.bookingSource,
  });
}

function onMessage(event: MessageEvent) {
  if (event.origin !== 'https://meetings.hubspot.com') return;
  if (typeof event.data?.height === 'number' && event.data.height > 200) {
    messagedHeight.value = Math.round(event.data.height);
  }
  if (!event.data?.meetingBookSucceeded) return;
  const contact =
    event.data?.meetingsPayload?.bookingResponse?.postResponse?.contact;
  gtmPush({
    event: 'hubspot_meeting_booked',
    booking_type: 'hubspot_meetings',
    booking_source: props.bookingSource,
    email: contact?.email || getPrefill().email,
  });
}

onMounted(() => {
  window.addEventListener('message', onMessage);
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
});
</script>

<style lang="scss" scoped>
.hs-meeting {
  position: relative;
  width: 100%;
  min-width: 320px;

  &__frame {
    width: 100%;
    height: 100%;
    border: 0;
  }
}

.loader {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--brand-color-primary);
  box-shadow: 32px 0 var(--brand-color-primary),
    -32px 0 var(--brand-color-primary);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  animation: flash 0.5s ease-out infinite alternate;

  @keyframes flash {
    0% {
      background-color: #fff2;
      box-shadow: 32px 0 #fff2, -32px 0 var(--brand-color-primary);
    }
    50% {
      background-color: var(--brand-color-primary);
      box-shadow: 32px 0 #fff2, -32px 0 #fff2;
    }
    100% {
      background-color: #fff2;
      box-shadow: 32px 0 var(--brand-color-primary), -32px 0 #fff2;
    }
  }
}
</style>
